import Stack from "@mui/material/Stack"

import TextInput from "@components/text_input"

import Button from "@components/button"
import { GREENISH, ORANGISH, REDISH } from "@constants/colors"
import {
  CustomerDetailFormData,
  useCustomerPageContext
} from "@contexts/customer"
import { updateCustomerMutation } from "@domain/customer/infos"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import styles from "./styles"

export default function CustomerDetailPage() {
  const {
    translate,
    currentCustomer,
    resetCustomerDetailForm,
    controlCustomerDetailForm,
    isValidCustomerDetailForm,
    isDirtyCustomerDetailForm,
    handleSubmitCustomerDetailForm
  } = useCustomerPageContext()

  const { id } = useParams()
  const customer = currentCustomer

  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  async function onSubmit(data: CustomerDetailFormData) {
    if (!customer?._id) return

    setIsLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await updateCustomerMutation({
      _id: customer?._id,
      companyName: data.name ? data.name : customer.companyName,
      accountConfirmation: data.accountConfirmation
        ? data.accountConfirmation
        : customer.accountConfirmation,
      accountStatus: data.accountStatus
        ? data.accountStatus
        : customer.accountStatus,
      appStatus: data.appStatus ? data.appStatus : customer.appStatus
    })
    if (!isSuccess || !responseData) {
      setIsLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    queryClient.invalidateQueries({ queryKey: ["customer-data-table"] })
    setIsLoading(false)
  }
  const customerId = customer?._id || id

  useEffect(() => {
    if (customerId) {
      resetCustomerDetailForm?.({
        name: customer?.companyName || "",
        accountConfirmation: customer?.accountConfirmation || "",
        accountStatus: customer?.accountStatus || "",
        appStatus: customer?.appStatus || ""
      })
    }
  }, [customerId, customer, resetCustomerDetailForm])

  const accountConfirmation = controlCustomerDetailForm._getWatch(
    "accountConfirmation"
  )

  const accountStatus = controlCustomerDetailForm._getWatch("accountStatus")

  const appStatus = controlCustomerDetailForm._getWatch("appStatus")

  /*
  const accountConfirmation = useWatch({
    control: controlCustomerDetailForm,
    name: "accountConfirmation",
    defaultValue: customer?.accountConfirmation ?? ""
  })
  const accountStatus = useWatch({
    control: controlCustomerDetailForm,
    name: "accountStatus"
  })
  const appStatus = useWatch({
    control: controlCustomerDetailForm,
    name: "appStatus"
  })
    */

  return (
    <Stack sx={styles.containerWidth} gap={2} py={2}>
      <Stack gap={2}>
        <TextInput<CustomerDetailFormData>
          key={`company-name-${customer?._id}`}
          type="text"
          name="name"
          control={controlCustomerDetailForm}
          label={translate("customer_detail_company_name_label")}
          placeholder={customer?.companyName ?? translate("enter")}
          defaultValue={customer?.companyName}
        />
        <TextInput<CustomerDetailFormData>
          key={`account-confirmation-${customer?._id}-${accountConfirmation}`}
          // key={`account-confirmation-${customer?._id}`}
          type="select"
          label={translate("customer_detail_account_confirmation_label")}
          name="accountConfirmation"
          control={controlCustomerDetailForm}
          /* onChange={(e) => {
            setValue("plan", String(e.target.value))
            trigger("plan")
          }} */
          sx={{
            "& .MuiInputBase-input": {
              color: accountConfirmation === "Approved" ? GREENISH : ORANGISH
            },
            "& .MuiSelect-select": {
              color: accountConfirmation === "Approved" ? GREENISH : ORANGISH
            },
            "& .MuiOutlinedInput-input": {
              color: accountConfirmation === "Approved" ? GREENISH : ORANGISH
            }
          }}
          placeholder={customer?.accountConfirmation ?? translate("enter")}
          defaultValue={customer?.accountConfirmation}
          selectOptions={[
            { label: "Waiting approval", value: "Waiting approval" },
            { label: "Approved", value: "Approved" }
          ]}
          required
        />
        <TextInput<CustomerDetailFormData>
          key={`account-status-${customer?._id}-${accountStatus}`}
          type="select"
          label={translate("customer_detail_account_status_label")}
          name="accountStatus"
          control={controlCustomerDetailForm}
          /* onChange={(e) => {
            setValue("plan", String(e.target.value))
            trigger("plan")
          }} */
          sx={{
            "& .MuiInputBase-input": {
              color: accountStatus === "Active" ? GREENISH : REDISH
            },
            "& .MuiSelect-select": {
              color: accountStatus === "Active" ? GREENISH : REDISH
            },
            "& .MuiOutlinedInput-input": {
              color: accountStatus === "Active" ? GREENISH : REDISH
            }
          }}
          placeholder={customer?.accountStatus ?? translate("enter")}
          defaultValue={customer?.accountStatus}
          selectOptions={[
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" }
          ]}
          required
        />
        <TextInput<CustomerDetailFormData>
          key={`app-status-${customer?._id}-${appStatus}`}
          type="select"
          label={translate("customer_detail_app_status_label")}
          name="appStatus"
          control={controlCustomerDetailForm}
          /* onChange={(e) => {
            setValue("plan", String(e.target.value))
            trigger("plan")
          }} */
          sx={{
            "& .MuiInputBase-input": {
              color: appStatus === "Enabled" ? GREENISH : REDISH
            },
            "& .MuiSelect-select": {
              color: appStatus === "Enabled" ? GREENISH : REDISH
            },
            "& .MuiOutlinedInput-input": {
              color: appStatus === "Enabled" ? GREENISH : REDISH
            }
          }}
          placeholder={customer?.appStatus ?? translate("enter")}
          defaultValue={customer?.appStatus}
          selectOptions={[
            { label: "Enabled", value: "Enabled" },
            { label: "Disabled", value: "Disabled" }
          ]}
          required
        />
        <Button
          loading={isLoading}
          disabled={!isValidCustomerDetailForm || !isDirtyCustomerDetailForm}
          onClick={handleSubmitCustomerDetailForm?.(onSubmit)}
          sx={{ width: "%25", marginLeft: "auto" }}
        >
          {translate("save")}
        </Button>
      </Stack>
    </Stack>
  )
}
